import { Directive, ElementRef, Input, HostListener } from '@angular/core';

import * as _ from 'lodash';

/**
 * Adiciona o efeito "ripple" ao clicar em um elemento
 */
export abstract class RippleDirectiveBase {

  @Input('no-ripple')
  private set set_no_ripple(no_ripple: boolean | string) {
    this.no_ripple = _.isString(no_ripple) && no_ripple == '' ? true : (_.isBoolean(no_ripple) ? (<boolean>no_ripple) : true);
  }

  constructor(
    public elementRef: ElementRef
  ) {}

  public set no_ripple(value: boolean) {
    this._no_ripple = value;
  }
  public get no_ripple(): boolean {
    return this._no_ripple;
  }

  /**
   * @ignore
   */
  protected _no_ripple = false;

  private _default_ripple_opacity = '0.15';
  private _animate_timeout: any;

  @HostListener('mousedown', ['$event']) public _onClick(e: MouseEvent) {
    if (this.no_ripple) { return; }

    const targetEl = this.elementRef ? this.elementRef.nativeElement : null;
    if (!targetEl) { return; }

    if (this._animate_timeout) { clearTimeout(this._animate_timeout); }

    let rippleEl = (<HTMLElement>targetEl.querySelector('.ripple'));

    if (!!rippleEl) {
      rippleEl.classList.remove('animate');
    } else {
      rippleEl = document.createElement('span');
      rippleEl.classList.add('ripple');
      targetEl.appendChild(rippleEl);
    }

    let offset_x = e.offsetX;
    let offset_y = e.offsetY;

    if (e.target != targetEl) {
      const relative_offset = this._relativeOffset(<HTMLElement>e.target, targetEl);

      offset_x += relative_offset.left;
      offset_y += relative_offset.top;
    }

    rippleEl.style.width = rippleEl.style.height = Math.max(targetEl.offsetWidth, targetEl.offsetHeight) + 'px';
    rippleEl.style.left = (offset_x - rippleEl.offsetWidth / 2) + 'px';
    rippleEl.style.top = (offset_y - rippleEl.offsetHeight / 2) + 'px';

    // Apply element color and background color to wrapper
    const element_style    = window.getComputedStyle(targetEl, null);
    const color           = element_style.color;

    const background_contrast_color = this._contrast(this._contrast(color));

    rippleEl.style.color = color;
    rippleEl.style.background = background_contrast_color;

    rippleEl.classList.add('animate');
    this._animate_timeout = setTimeout(() => rippleEl.classList.remove('animate'), 650);
  }

  private _contrast(color: string, bw: boolean = true): string {

    let colorHex = this._toHexadecimal(color);

    if (! /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(colorHex)) {
      return '#FFFFFF';
    }

    if (colorHex.length === 4) {
      colorHex = colorHex.replace(/^#(.)(.)(.)/i, '#$1$1$2$2$3$3');
    }

    const r = parseInt(colorHex.substr(1, 2), 16);
    const g = parseInt(colorHex.substr(3, 2), 16);
    const b = parseInt(colorHex.substr(5, 2), 16);

    if (bw) {
      // https://github.com/lffg/yiq/blob/master/index.js
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return this._toRGBA(yiq >= 128 ? '#000000' : '#FFFFFF');
    }

    return this._toRGBA(`#${this._padZero(r)}${this._padZero(g)}${this._padZero(b)}`);
  }

  private _toHexadecimal(color: string, allow_alpha: boolean = false): string {
    if (!color || (color && color.indexOf('(') == -1)) { return color; }

    const parts = color.replace(/(rgba|rgb)/g, '').replace(/[\(\)]/g, '').split(',').map((part) => _.trim(part));

    const r = this._padZero(parseInt(parts[0], 10).toString(16));
    const g = this._padZero(parseInt(parts[1], 10).toString(16));
    const b = this._padZero(parseInt(parts[2], 10).toString(16));

    if (parts[3] && allow_alpha) {
      const a = parseFloat(parseFloat(parts[3].substring(0, parts[3].length - 1)).toFixed(2));
      return `#${r}${g}${b}${this._padZero((a * 255).toString(16).substring(0, 2))}`;
    }

    return `#${r}${g}${b}`;
  }

  private _toRGBA(color: string, opacity: string = this._default_ripple_opacity) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    return result ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})` : color;
  }

  private _padZero(str: string | number, len: number = 2): string {
    if (_.isNumber(str)) { str = str.toString(); }
    const zeros = new Array(len).join('0');
    return (zeros + str).slice(-len);
  }

  private _relativeOffset(child_element: HTMLElement, parent_element: HTMLElement) {
    const offset_parent_element = (<HTMLElement>child_element.offsetParent);
    const parent_rect = offset_parent_element.getBoundingClientRect();
    const child_rect = child_element.getBoundingClientRect();

    const offset = {
      top: child_rect.top - parent_rect.top,
      left: child_rect.left - parent_rect.left
    }

    // Reached top of document
    if (offset_parent_element.tagName === 'BODY') { return offset; }

    // Parent element contains the 'top' element we want the offset to be relative to
    if (child_element.parentElement === parent_element) { return offset; }

    // Reached the 'top' element we want the offset to be relative to
    if (offset_parent_element === parent_element) { return offset; }

    // Get parent's relative offset
    const parent_offset = this._relativeOffset(offset_parent_element, parent_element);
    offset.top += parent_offset.top;
    offset.left += parent_offset.left;
    return offset;
  }
}

@Directive({
  selector: '[with-ripple], .with-ripple'
})
export class RippleDirective extends RippleDirectiveBase {
  constructor(
    public elementRef: ElementRef
  ) {
    super(elementRef)
  }
}
